/**
 * GPS Utilities for calculating distances and bearings between coordinates
 */

/**
 * Calculate the distance between two GPS coordinates using the Haversine formula
 * @param lat1 Latitude of first point in degrees
 * @param lng1 Longitude of first point in degrees
 * @param lat2 Latitude of second point in degrees
 * @param lng2 Longitude of second point in degrees
 * @returns Distance in meters
 */
export function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Calculate the bearing (azimuth) from point A to point B
 * @param lat1 Latitude of point A in degrees
 * @param lng1 Longitude of point A in degrees
 * @param lat2 Latitude of point B in degrees
 * @param lng2 Longitude of point B in degrees
 * @returns Bearing in degrees (0° = North, 90° = East, 180° = South, 270° = West)
 */
export function calculateBearing(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLng = toRadians(lng2 - lng1);
  const lat1Rad = toRadians(lat1);
  const lat2Rad = toRadians(lat2);

  const y = Math.sin(dLng) * Math.cos(lat2Rad);
  const x =
    Math.cos(lat1Rad) * Math.sin(lat2Rad) - Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);

  let bearing = toDegrees(Math.atan2(y, x));
  bearing = (bearing + 360) % 360; // Normalize to 0-360

  return bearing;
}

/**
 * Check if a point lies between two other points using distance comparison
 * If point C is between A and B, then distance(A→B) ≈ distance(A→C) + distance(C→B)
 * @param lat1 Latitude of point A
 * @param lng1 Longitude of point A
 * @param lat2 Latitude of point B
 * @param lng2 Longitude of point B
 * @param checkLat Latitude of point C to check
 * @param checkLng Longitude of point C to check
 * @param threshold Distance threshold in meters (default: 10 meters)
 * @returns true if point C is between A and B
 */
export function isPointBetweenPoints(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
  checkLat: number,
  checkLng: number,
  threshold: number = 10,
): boolean {
  // Calculate distances
  const distAB = calculateDistance(lat1, lng1, lat2, lng2);
  const distAC = calculateDistance(lat1, lng1, checkLat, checkLng);
  const distCB = calculateDistance(checkLat, checkLng, lat2, lng2);

  // If C is between A and B, then AC + CB should be approximately equal to AB
  // We also need to ensure C is not too close to A or B (within threshold)
  const sumDist = distAC + distCB;
  const diff = Math.abs(sumDist - distAB);

  // Check if the difference is within threshold AND C is not at A or B
  return diff <= threshold && distAC > threshold && distCB > threshold;
}

/**
 * Convert degrees to radians
 */
function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Convert radians to degrees
 */
function toDegrees(radians: number): number {
  return radians * (180 / Math.PI);
}
